/**
 * Componente de Ruta Protegida
 *
 * Protege rutas que requieren autenticación y roles específicos
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useUserRole } from '../hooks/useUserRole';

function ProtectedRoute({ children, allowedRoles }) {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { role, loading: roleLoading, canAccessAdminPanel } = useUserRole();
  const location = useLocation();

  const loading = authLoading || roleLoading;

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: '#7e4fb9',
        }}
      >
        <div>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #7e4fb9',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          ></div>
          <p>{t('common.verifyingPermissions')}</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    // Redirigir a login guardando la ubicación actual
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // RES-93: el partner solo entra a su portal; cualquier otra ruta lo lleva ahí
  if (role === 'PARTNER') {
    if (allowedRoles && allowedRoles.includes('PARTNER')) return children;
    return <Navigate to="/partner" replace />;
  }

  // Verificar si el usuario puede acceder al admin panel
  const hasAccess = canAccessAdminPanel();

  if (!hasAccess) {
    console.warn('[ProtectedRoute] User cannot access admin panel:', { role });
    return <Navigate to="/unauthorized" replace />;
  }

  // Si se especifican roles permitidos, verificar si el usuario tiene uno de ellos
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(role)) {
      console.warn('[ProtectedRoute] User does not have required role:', {
        userRole: role,
        allowedRoles,
      });
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
}

export default ProtectedRoute;
